import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import {
  Download,
  LogOut,
  Search,
  Trash2,
  FileText,
  CreditCard,
  X,
  ClipboardList,
  Maximize,
  RefreshCw 
} from "lucide-react";
import { databases, account, storage } from "./appwrite";
import { Query } from "appwrite";

// Configuration
const DB_ID = process.env.REACT_APP_APPWRITE_DB_ID || "regdb";
const COL_ID = "692a9f82002dabe4ac9c"; 
const BUCKET_ID = "mmttcregid";

export default function AdminDashboard({ onLogout, onViewAttendance }) {
  const [registrations, setRegistrations] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  // Modals
  const [showQR, setShowQR] = useState(false);
  const [activeSessionQR, setActiveSessionQR] = useState(1);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState(null);
  const [qrToken, setQrToken] = useState(Date.now()); // Rotating token

  const navigate = useNavigate();

  // 1. Rotate Token every 5 seconds
  useEffect(() => {
    let interval;
    if (showQR) {
        interval = setInterval(() => {
            setQrToken(Date.now());
        }, 5000);
    }
    return () => clearInterval(interval);
  }, [showQR]);

  // 2. 🔥 FIXED URL GENERATION LOGIC
  const today = new Date();
  const offset = today.getTimezoneOffset();
  const todayISO = new Date(today.getTime() - offset * 60 * 1000).toISOString().split("T")[0];
  
  const base = window.location.origin + "/attendance";
  
  // ✅ FIX: Explicitly use 'session=' and 'date=' keys.
  // Previous error was likely caused by shorthand 's2' which the reader couldn't parse.
  const activeQRLink = `${base}?session=${activeSessionQR}&date=${todayISO}&ts=${qrToken}`;

  // ... (Fetch Data, Logout, Delete, Export, etc. - Same as before)
  useEffect(() => { fetchData(); }, []);
   const fetchData = async () => {
    try {
      const res = await databases.listDocuments(DB_ID, COL_ID, [Query.limit(500), Query.orderDesc("$createdAt")]);
      setRegistrations(res.documents);
    } catch (err) { console.error(err); }
  };
  const handleLogout = async () => { try { await account.deleteSession("current"); } catch (err) {} onLogout(); };
 const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this registration?"))
      return;
    try {
      await databases.deleteDocument(DB_ID, COL_ID, id);
      fetchData(); 
    } catch (err) {
      alert("Failed to delete: " + err.message);
    }
  };
    const getViewUrl = (fileId) => { try { if (!storage) return ""; return storage.getFileView(BUCKET_ID, fileId); } catch (e) { return ""; } };

    
    const handleExport = () => {
    if (registrations.length === 0) {
      alert("No data to export.");
      return;
    }

    const headers = [
      "Name",
      "Email",
      "Phone",
      "Designation",
      "Department",
      "Affiliation",
      "NOC Uploaded",
      "Photo Uploaded",
      "WhatsApp",
      "DOB",
      "Contact Method",
      "Registered Date",
    ];

    const rows = registrations.map((r) => {
      const safe = (str) => `"${(str || "").replace(/"/g, '""')}"`;
      return [
        safe(r.name),
        safe(r.email),
        safe(r.phone),
        safe(r.designation),
        safe(r.department),
        safe(r.affiliation),
        r.nocFileId ? "Yes" : "No",
        r.photoFileId ? "Yes" : "No",
        safe(r.whatsapp),
        safe(r.dob),
        safe(r.contactMethod),
        safe(r.$createdAt ? new Date(r.$createdAt).toLocaleString() : ""),
      ].join(",");
    });
        const csvContent = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `registrations_${todayISO}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  // Filtered Registrations based on Search
  const filtered = registrations.filter(r => 
      r.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
      r.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 min-h-screen bg-gray-50">
      <div className="bg-white rounded-3xl shadow-xl p-8 mb-8 border border-gray-100">
        
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
          <div><h2 className="text-3xl font-bold text-gray-900">Dashboard</h2></div>
          <div className="flex gap-3 flex-wrap justify-center">
            <button onClick={onViewAttendance} className="flex items-center bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 shadow-sm">
              <ClipboardList className="w-4 h-4 mr-2" /> View Attendance Logs
            </button>
            <button onClick={handleExport} className="flex items-center bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 shadow-sm">
              <Download className="w-4 h-4 mr-2" /> Export CSV
            </button>
            <button onClick={() => setShowQR(true)} className="flex items-center bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 shadow-sm">
              <Maximize className="w-4 h-4 mr-2" /> Project QR Code
            </button>
            <button onClick={handleLogout} className="flex items-center bg-red-50 text-red-600 px-4 py-2 rounded-lg hover:bg-red-100 border border-red-100">
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </button>
          </div>
        </div>

        {/* Stats & Search */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <div className="bg-blue-50 p-6 rounded-2xl border border-blue-100">
            <p className="text-blue-600 font-medium">Total Registrations</p>
            <p className="text-4xl font-bold text-gray-900">{registrations.length}</p>
          </div>
          <div className="bg-indigo-50 p-6 rounded-2xl border border-indigo-100">
            <p className="text-indigo-600 font-medium">Seats Remaining</p>
            <p className="text-4xl font-bold text-gray-900">{Math.max(0, 30 - registrations.length)}</p>
          </div>
        </div>

        <div className="relative mb-6">
          <Search className="absolute left-4 top-3.5 text-gray-400 w-5 h-5" />
          <input type="text" placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-100 outline-none" />
        </div>

        {/* Table */}
        <div className="overflow-x font-sans border border-gray-200 rounded-xl shadow-sm">
          <table className="w-100 text-left border">
            <thead>
              <tr className="bg-blue-100 text-gray-600 text-xs font-sans uppercase font-semibold">
                <th className="p-4">Photo</th>
                <th className="p-4">Name</th>
                <th className="p-4">Email</th>
                <th className="p-4">Info</th>
                <th className="p-4">Documents</th>
                <th className="p-4">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 text-sm">
              {filtered.map((r) => (
                <tr key={r.$id} className="hover:bg-gray-50 transition-colors">
                  <td className="p-4">
                    {r.photoFileId ? 
                      <img src={getViewUrl(r.photoFileId)} alt="usr" className="w-10 h-10 object-cover rounded-full border cursor-pointer" onClick={() => { setPhotoPreviewUrl(getViewUrl(r.photoFileId)); setShowPhotoModal(true); }} /> 
                      : <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center text-gray-400 text-xs">N/A</div>
                    }
                  </td>
                  <td className="p-4 font-medium">{r.name}</td>
                  <td className="p-4 text-gray-600">{r.email}</td>
                  <td className="p-4 text-gray-500 text-xs">
                    <div>{r.designation}</div>
                    <div>{r.affiliation}</div>
                  </td>
                  <td className="p-4">
                    {r.nocFileId ? <a href={getViewUrl(r.nocFileId)} target="_blank" rel="noreferrer" className="text-blue-600 underline text-xs flex items-center"><FileText size={12} className="mr-1"/> NOC</a> : <span className="text-gray-400 text-xs">-</span>}
                  </td>
                  <td className="p-4 flex gap-2">
                    <button onClick={() => navigate(`/id-card/${r.$id}`)} className="text-blue-500 hover:bg-blue-50 p-2 rounded"><CreditCard size={16} /></button>
                    <button onClick={() => handleDelete(r.$id)} className="text-red-500 hover:bg-red-50 p-2 rounded"><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Photo Modal */}
      {showPhotoModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[999] p-4" onClick={() => setShowPhotoModal(false)}>
          <img src={photoPreviewUrl} alt="full" className="max-h-[90vh] rounded-lg border-4 border-white" />
        </div>
      )}

      {/* --- BIG QR Codes Modal (Projector Mode) --- */}
      {showQR && (
        <div className="fixed inset-0 bg-white z-[999] flex flex-col animate-fadeIn">
          {/* Top Bar */}
          <div className="flex justify-between items-center p-6 border-b border-gray-200 bg-white shadow-sm">
            <div>
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                    <Maximize className="w-6 h-6 text-indigo-600" />
                    Attendance Projector
                </h2>
                <div className="flex items-center gap-3 mt-1">
                    <p className="text-sm text-gray-500">Date: <span className="font-mono font-bold text-indigo-600">{todayISO}</span></p>
                    <span className="flex items-center text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                        <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> Live Updating
                    </span>
                </div>
            </div>
            
            <div className="flex bg-gray-100 p-1.5 rounded-xl gap-1">
                {[1, 2, 3, 4].map(num => (
                    <button key={num} onClick={() => setActiveSessionQR(num)} className={`px-6 py-2 rounded-lg font-bold transition-all ${activeSessionQR === num ? "bg-white text-indigo-600 shadow-md transform scale-105" : "text-gray-500 hover:text-gray-700 hover:bg-gray-200"}`}>
                        Session {num}
                    </button>
                ))}
            </div>

            <button onClick={() => setShowQR(false)} className="p-3 bg-red-50 text-red-600 hover:bg-red-100 rounded-full transition-colors"><X className="w-8 h-8" /></button>
          </div>

          {/* Centered QR */}
          <div className="flex-1 flex flex-col items-center justify-center bg-slate-50 p-10">
                <div className="bg-white p-12 rounded-[3rem] shadow-2xl flex flex-col items-center border border-gray-200 transform transition-all duration-300">
                    <h1 className="text-5xl font-black text-indigo-900 mb-8 tracking-tight">SCAN TO MARK ATTENDANCE</h1>
                    
                    <div className="bg-white p-4 border-4 border-indigo-100 rounded-3xl mb-8 relative">
                        {/* 450px size for 10ft+ visibility */}
                        <QRCodeSVG value={activeQRLink} size={450} level="H" includeMargin={true} />
                        <div className="absolute top-2 right-2 w-4 h-4 bg-green-500 rounded-full animate-ping"></div>
                    </div>

                    <div className="flex items-center gap-4">
                        <span className="bg-indigo-100 text-indigo-800 px-6 py-2 rounded-full text-xl font-bold">SESSION {activeSessionQR}</span>
                        <span className="text-gray-400 text-2xl">|</span>
                        <span className="text-2xl font-mono text-gray-600 font-semibold">{today.toLocaleDateString("en-IN", { weekday: 'long', day: 'numeric', month: 'long' })}</span>
                    </div>
                </div>
                {/* Debug Link Visualizer */}
                <p className="mt-8 text-gray-400 font-mono text-xs opacity-50 max-w-2xl text-center break-all">{activeQRLink}</p>
          </div>
        </div>
      )}
    </div>
  );
}